import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Input,
    Row,
    Nav,
    NavItem,
    NavLink,
    Table,
} from "reactstrap";
import moment from "moment";
import { LoadingOverlay } from "../../util/Helpers";
import { ButtonStyles } from "../../constants/ButtonStyles";
import {
    emailLoadReq,
    fetchEmails,
    getEmailsByDateRange,
    extractEmailDetails,
    extractEmailDetailsByStatus,
    verifyEmailDetails,
    processEmailDetails,
} from "../../util/APIUtils";

const getToday = function () {
    return new Date().toISOString().substring(0, 10);
};

const toNumber = function (value) {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
        return 0;
    }
    return parsed;
};

const toTwoDecimals = function (value) {
    return parseFloat(toNumber(value).toFixed(2));
};

const normalizeExtractResponse = function (response) {
    const safeData = response && response.data && Array.isArray(response.data) ? response.data : [];
    const rows = safeData.map(function (item, index) {
        const invoiceValue = toTwoDecimals(item.invoiceValue);
        const creditedAmount = toTwoDecimals(item.creditedAmount);
        const difference = toTwoDecimals(item.difference);
        const rowId = item && item.id !== undefined && item.id !== null ? String(item.id) : (item.messageId || String(index));

        return {
            id: item && item.id !== undefined && item.id !== null ? item.id : null,
            rowId: rowId,
            messageId: item.messageId || "",
            invoiceNumber: item.invoiceNumber || "",
            invoiceValue: invoiceValue,
            creditedAmount: creditedAmount,
            transactionReference: item.transactionReference || "",
            remarks: item.remarks || "",
            balance: toNumber(item.balance),
            difference: difference,
            status: item.status || "NEW",
            isEditing: false,
            originalInvoiceNumber: item.invoiceNumber || "",
            originalInvoiceValue: invoiceValue,
        };
    });

    return {
        summary: {
            status: response && response.status ? response.status : "SUCCESS",
            emailAddress: response && response.emailAddress ? response.emailAddress : "",
            startDate: response && response.startDate ? response.startDate : "",
            endDate: response && response.endDate ? response.endDate : "",
            senderEmailaddress: response && response.senderEmailaddress ? response.senderEmailaddress : "",
            totalEmails: response && response.totalEmails ? response.totalEmails : 0,
            savedCount: response && response.savedCount ? response.savedCount : 0,
            duplicateCount: response && response.duplicateCount ? response.duplicateCount : 0,
            errorCount: response && response.errorCount ? response.errorCount : 0,
        },
        rows: rows,
    };
};

export default function EmailSyncCenter() {
    const [activeTab, setActiveTab] = useState(1);
    const [block, setBlock] = useState(false);
    const [apiBusy, setApiBusy] = useState(false);
    const [loadOptions, setLoadOptions] = useState({
        sourceEmails: [],
        fromAddresses: [],
    });

    const [fetchForm, setFetchForm] = useState({
        emailAddress: "",
        fromAddress: "",
        date: getToday(),
        password: "",
    });
    const [fetchResult, setFetchResult] = useState(null);

    const [contentForm, setContentForm] = useState({
        emailAddress: "",
        startDate: getToday(),
        endDate: getToday(),
        senderEmailaddress: "",
    });
    const [emails, setEmails] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);
    const [contentLocked, setContentLocked] = useState(false);

    const [extractLoading, setExtractLoading] = useState(false);
    const [extractSummary, setExtractSummary] = useState(null);
    const [extractRows, setExtractRows] = useState([]);

    useEffect(function () {
        loadDropdownOptions();
    }, []);

    useEffect(
        function () {
            if (fetchForm.emailAddress && !contentForm.emailAddress) {
                setContentForm(function (prev) {
                    return {
                        emailAddress: fetchForm.emailAddress,
                        startDate: prev.startDate,
                        endDate: prev.endDate,
                        senderEmailaddress: prev.senderEmailaddress,
                    };
                });
            }
        },
        [fetchForm.emailAddress]
    );

    const handleTabChange = function (tab) {
        setActiveTab(tab);
        if (tab === 3 && extractRows.length === 0) {
            loadExtractDetailsByStatus();
        }
    };

    const loadDropdownOptions = function () {
        setBlock(true);
        setApiBusy(true);
        emailLoadReq()
            .then(function (response) {
                if (response.status === "SUCCESS" && response.data) {
                    const sourceEmails = response.data.filter(function (item) {
                        return item.category === "sourceEmail";
                    });
                    const fromAddresses = response.data.filter(function (item) {
                        return item.category === "fromAddress";
                    });

                    const firstSource = sourceEmails.length > 0 ? sourceEmails[0].key : "";
                    const firstFrom = fromAddresses.length > 0 ? fromAddresses[0].key : "";

                    setLoadOptions({
                        sourceEmails: sourceEmails,
                        fromAddresses: fromAddresses,
                    });

                    setFetchForm(function (prev) {
                        return {
                            emailAddress: firstSource,
                            fromAddress: firstFrom,
                            date: prev.date,
                            password: prev.password,
                        };
                    });

                    setContentForm(function (prev) {
                        return {
                            emailAddress: firstSource,
                            startDate: prev.startDate,
                            endDate: prev.endDate,
                            senderEmailaddress: firstFrom,
                        };
                    });
                }
                setBlock(false);
                setApiBusy(false);
            })
            .catch(function (error) {
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Error",
                    text: "Failed to load dropdown options: " + error.message,
                });
            });
    };

    const handleFetchFormChange = function (e) {
        const name = e.target.name;
        const value = e.target.value;
        setFetchForm(function (prev) {
            return {
                emailAddress: name === "emailAddress" ? value : prev.emailAddress,
                fromAddress: name === "fromAddress" ? value : prev.fromAddress,
                date: name === "date" ? value : prev.date,
                password: name === "password" ? value : prev.password,
            };
        });
    };

    const handleFetchSubmit = function () {
        if (!fetchForm.emailAddress || !fetchForm.fromAddress || !fetchForm.date || !fetchForm.password) {
            swal({
                icon: "warning",
                title: "Validation Error",
                text: "Please fill all required fields",
            });
            return;
        }

        if (fetchForm.password.length !== 16) {
            swal({
                icon: "warning",
                title: "Validation Error",
                text: "Password must be exactly 16 characters",
            });
            return;
        }

        setBlock(true);
        setApiBusy(true);
        fetchEmails({
            emailAddress: fetchForm.emailAddress,
            date: fetchForm.date,
            fromAddress: fetchForm.fromAddress,
            password: fetchForm.password,
        })
            .then(function (response) {
                setBlock(false);
                setApiBusy(false);
                setFetchResult(response);
                swal({
                    icon: "success",
                    title: response.status,
                    text:
                        "Total Emails: " +
                        response.totalEmailCount +
                        " | Stored Emails: " +
                        response.storedEmailCount +
                        " | Message: " +
                        response.message,
                });
            })
            .catch(function (error) {
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch emails: " + error.message,
                });
            });
    };

    const handleFetchClear = function () {
        const sourceEmailKey = loadOptions.sourceEmails.length > 0 ? loadOptions.sourceEmails[0].key : "";
        const fromAddressKey = loadOptions.fromAddresses.length > 0 ? loadOptions.fromAddresses[0].key : "";
        setFetchForm({
            emailAddress: sourceEmailKey,
            fromAddress: fromAddressKey,
            date: getToday(),
            password: "",
        });
        setFetchResult(null);
    };

    const handleContentFormChange = function (e) {
        const name = e.target.name;
        const value = e.target.value;
        setContentForm(function (prev) {
            return {
                emailAddress: name === "emailAddress" ? value : prev.emailAddress,
                startDate: name === "startDate" ? value : prev.startDate,
                endDate: name === "endDate" ? value : prev.endDate,
                senderEmailaddress: name === "senderEmailaddress" ? value : prev.senderEmailaddress,
            };
        });
        setEmails([]);
    };

    const handleContentSubmit = function () {
        if (!contentForm.emailAddress || !contentForm.startDate || !contentForm.endDate) {
            swal({
                icon: "warning",
                title: "Validation Error",
                text: "Please fill required fields (Email, Start Date, and End Date)",
            });
            return;
        }

        if (contentForm.startDate > contentForm.endDate) {
            swal({
                icon: "warning",
                title: "Date Error",
                text: "Start Date cannot be after End Date",
            });
            return;
        }

        setEmails([]);
        setContentLocked(true);
        setContentLoading(true);
        setBlock(true);
        setApiBusy(true);
        getEmailsByDateRange({
            emailAddress: contentForm.emailAddress,
            startDate: contentForm.startDate,
            endDate: contentForm.endDate,
            senderEmailaddress: contentForm.senderEmailaddress,
        })
            .then(function (response) {
                setContentLoading(false);
                setBlock(false);
                setApiBusy(false);
                if (response.emails && Array.isArray(response.emails)) {
                    setEmails(response.emails);
                    swal({
                        icon: "success",
                        title: "Success",
                        text: "Found " + response.emails.length + " email(s)",
                    });
                } else {
                    setEmails([]);
                    swal({
                        icon: "info",
                        title: "No Results",
                        text: "No emails found for the selected criteria",
                    });
                }
            })
            .catch(function (error) {
                setContentLoading(false);
                setContentLocked(false);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch email content: " + error.message,
                });
            });
    };

    const handleContentClear = function () {
        const defaultFromAddress = loadOptions.fromAddresses.length > 0 ? loadOptions.fromAddresses[0].key : "";
        setContentForm({
            emailAddress: contentForm.emailAddress,
            startDate: getToday(),
            endDate: getToday(),
            senderEmailaddress: defaultFromAddress,
        });
        setEmails([]);
        setContentLocked(false);
    };

    const handleExtract = function () {
        if (!contentForm.emailAddress || !contentForm.startDate || !contentForm.endDate || !contentForm.senderEmailaddress) {
            swal({
                icon: "warning",
                title: "Validation Error",
                text: "Email, Start Date, End Date and Sender Email are mandatory for Extract",
            });
            return;
        }

        setExtractLoading(true);
        setBlock(true);
        setApiBusy(true);
        extractEmailDetails({
            emailAddress: contentForm.emailAddress,
            startDate: contentForm.startDate,
            endDate: contentForm.endDate,
            senderEmailaddress: contentForm.senderEmailaddress,
        })
            .then(function (response) {
                setExtractLoading(false);
                setBlock(false);
                setApiBusy(false);
                const normalized = normalizeExtractResponse(response);
                setExtractSummary(normalized.summary);
                setExtractRows(normalized.rows);
                setActiveTab(3);
                swal({
                    icon: "success",
                    title: "Extract Completed",
                    text:
                        "Total: " +
                        normalized.summary.totalEmails +
                        " | Saved: " +
                        normalized.summary.savedCount +
                        " | Duplicate: " +
                        normalized.summary.duplicateCount +
                        " | Error: " +
                        normalized.summary.errorCount,
                });
            })
            .catch(function (error) {
                setExtractLoading(false);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Extract Failed",
                    text: error.message,
                });
            });
    };

    const loadExtractDetailsByStatus = function () {
        setExtractLoading(true);
        setBlock(true);
        setApiBusy(true);
        extractEmailDetailsByStatus()
            .then(function (response) {
                setExtractLoading(false);
                setBlock(false);
                setApiBusy(false);
                const normalized = normalizeExtractResponse(response);
                setExtractSummary(normalized.summary);
                setExtractRows(normalized.rows);
            })
            .catch(function (error) {
                setExtractLoading(false);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Error",
                    text: "Unable to load extracted details: " + error.message,
                });
            });
    };

    const buildVerifyRemarks = function (row) {
        const changes = [];
        const originalInvoiceNumber = row.originalInvoiceNumber || "";
        const currentInvoiceNumber = row.invoiceNumber || "";
        const originalInvoiceValue = toTwoDecimals(row.originalInvoiceValue);
        const currentInvoiceValue = toTwoDecimals(row.invoiceValue);

        if (originalInvoiceNumber !== currentInvoiceNumber) {
            changes.push("user corrected invoice number from " + originalInvoiceNumber + " to " + currentInvoiceNumber);
        }

        if (originalInvoiceValue !== currentInvoiceValue) {
            changes.push("user modified invoice amount from " + originalInvoiceValue.toFixed(2) + " to " + currentInvoiceValue.toFixed(2));
        }

        if (changes.length === 0) {
            return row.remarks || "";
        }

        const existingRemarks = row.remarks ? row.remarks.trim() : "";
        const appendedText = changes.join("; ");
        return existingRemarks ? existingRemarks + " | " + appendedText : appendedText;
    };

    const handleExtractRowChange = function (rowId, field, value) {
        const rows = extractRows.map(function (row) {
            if (row.rowId !== rowId) {
                return row;
            }

            const updated = Object.assign({}, row);
            updated[field] = value;

            if (field === "invoiceValue") {
                const inv = toTwoDecimals(value);
                const credit = toTwoDecimals(updated.creditedAmount);
                const diff = toTwoDecimals(inv - credit);
                updated.invoiceValue = inv;
                updated.difference = diff;
            }

            return updated;
        });

        setExtractRows(rows);
    };

    const toggleEditRow = function (rowId) {
        const rows = extractRows.map(function (row) {
            if (row.rowId === rowId) {
                const updated = Object.assign({}, row);
                updated.isEditing = !row.isEditing;
                if (!updated.isEditing) {
                    const inv = toTwoDecimals(updated.invoiceValue);
                    const credit = toTwoDecimals(updated.creditedAmount);
                    const diff = toTwoDecimals(inv - credit);
                    updated.difference = diff;
                }
                return updated;
            }
            return row;
        });

        setExtractRows(rows);
    };

    const verifyRow = function (rowId) {
        const selected = extractRows.find(function (row) {
            return row.rowId === rowId;
        });

        if (!selected) {
            return;
        }

        const updatedRemarks = buildVerifyRemarks(selected);
        const requestPayload = {
            id: selected.id,
            messageId: selected.messageId,
            invoiceNumber: selected.invoiceNumber,
            invoiceValue: toTwoDecimals(selected.invoiceValue),
            creditedAmount: toTwoDecimals(selected.creditedAmount),
            transactionReference: selected.transactionReference,
            remarks: updatedRemarks,
            balance: toTwoDecimals(selected.balance),
            difference: toTwoDecimals(selected.difference),
            status: selected.status,
            emailAddress: contentForm.emailAddress,
            startDate: contentForm.startDate,
            endDate: contentForm.endDate,
            senderEmailaddress: contentForm.senderEmailaddress,
        };

        setBlock(true);
        setApiBusy(true);
        verifyEmailDetails(requestPayload)
            .then(function () {
                const rows = extractRows.map(function (row) {
                    if (row.rowId === rowId) {
                        const updated = Object.assign({}, row);
                        updated.remarks = updatedRemarks;
                        updated.originalInvoiceNumber = updated.invoiceNumber;
                        updated.originalInvoiceValue = toTwoDecimals(updated.invoiceValue);
                        updated.isEditing = false;
                        updated.status = "VERIFIED";
                        updated.difference = toTwoDecimals(updated.invoiceValue - updated.creditedAmount);
                        return updated;
                    }
                    return row;
                });

                setExtractRows(rows);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "success",
                    title: "Verified",
                    text: "Row verified successfully",
                });
            })
            .catch(function (error) {
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Verify Failed",
                    text: error.message,
                });
            });
    };

    const buildProcessPayloadItem = function (row) {
        return {
            id: row.id,
            messageId: row.messageId,
            invoiceNumber: row.invoiceNumber,
            invoiceValue: toTwoDecimals(row.invoiceValue),
            creditedAmount: toTwoDecimals(row.creditedAmount),
            transactionReference: row.transactionReference,
            remarks: row.remarks,
            balance: toTwoDecimals(row.balance),
            difference: toTwoDecimals(row.difference),
            status: row.status,
            emailAddress: contentForm.emailAddress,
            startDate: contentForm.startDate,
            endDate: contentForm.endDate,
            senderEmailaddress: contentForm.senderEmailaddress,
        };
    };

    const processAllRows = function () {
        if (extractRows.length === 0) {
            swal({
                icon: "info",
                title: "No Records",
                text: "No rows available to process",
            });
            return;
        }

        const requestPayload = extractRows.map(function (row) {
            return buildProcessPayloadItem(row);
        });

        setBlock(true);
        setApiBusy(true);
        processEmailDetails(requestPayload)
            .then(function () {
                const rows = extractRows.map(function (row) {
                    const updated = Object.assign({}, row);
                    updated.status = "PROCESSED";
                    return updated;
                });
                setExtractRows(rows);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "success",
                    title: "Processed",
                    text: "All rows processed successfully",
                });
                loadExtractDetailsByStatus();
            })
            .catch(function (error) {
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Process Failed",
                    text: error.message,
                });
            });
    };

    const processRow = function (rowId) {
        const selected = extractRows.find(function (row) {
            return row.rowId === rowId;
        });

        if (!selected) {
            return;
        }

        const requestPayload = [buildProcessPayloadItem(selected)];

        setBlock(true);
        setApiBusy(true);
        processEmailDetails(requestPayload)
            .then(function () {
                const rows = extractRows.map(function (row) {
                    if (row.rowId === rowId) {
                        const updated = Object.assign({}, row);
                        updated.status = "PROCESSED";
                        return updated;
                    }
                    return row;
                });
                setExtractRows(rows);
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "success",
                    title: "Processed",
                    text: "Selected row processed successfully",
                });
                loadExtractDetailsByStatus();
            })
            .catch(function (error) {
                setBlock(false);
                setApiBusy(false);
                swal({
                    icon: "error",
                    title: "Process Failed",
                    text: error.message,
                });
            });
    };

    const renderFetchTab = function () {
        return (
            <div>
                <Row className="mb-4">
                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                Email Address <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="select"
                            name="emailAddress"
                            value={fetchForm.emailAddress}
                            onChange={handleFetchFormChange}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        >
                            <option value="">-- Select Email --</option>
                            {loadOptions.sourceEmails.map(function (email) {
                                return (
                                    <option key={email.id} value={email.key}>
                                        {email.value} ({email.key})
                                    </option>
                                );
                            })}
                        </Input>
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                From Address <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="select"
                            name="fromAddress"
                            value={fetchForm.fromAddress}
                            onChange={handleFetchFormChange}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        >
                            <option value="">-- Select Address --</option>
                            {loadOptions.fromAddresses.map(function (address) {
                                return (
                                    <option key={address.id} value={address.key}>
                                        {address.value} ({address.key})
                                    </option>
                                );
                            })}
                        </Input>
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                Date <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="date"
                            name="date"
                            value={fetchForm.date}
                            onChange={handleFetchFormChange}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        />
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                Password (16 Characters) <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="password"
                            name="password"
                            value={fetchForm.password}
                            onChange={handleFetchFormChange}
                            maxLength="16"
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        />
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col md="12">
                        <Button style={ButtonStyles.primarySm} onClick={handleFetchSubmit} className="mr-2" disabled={apiBusy}>
                            <i className="fa fa-paper-plane" style={{ marginRight: "5px" }}></i>
                            Submit
                        </Button>
                        <Button style={ButtonStyles.secondarySm} onClick={handleFetchClear} disabled={apiBusy}>
                            <i className="fa fa-times" style={{ marginRight: "5px" }}></i>
                            Clear
                        </Button>
                    </Col>
                </Row>

                {fetchResult && (
                    <Card
                        style={{
                            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            borderRadius: "12px",
                            border: "none",
                            padding: "20px",
                            color: "#fff",
                        }}
                    >
                        <Row>
                            <Col md="4">
                                <div style={{ fontSize: "13px", opacity: "0.9" }}>Total Emails</div>
                                <div style={{ fontSize: "32px", fontWeight: "700" }}>{fetchResult.totalEmailCount}</div>
                            </Col>
                            <Col md="4">
                                <div style={{ fontSize: "13px", opacity: "0.9" }}>Stored Emails</div>
                                <div style={{ fontSize: "32px", fontWeight: "700" }}>{fetchResult.storedEmailCount}</div>
                            </Col>
                            <Col md="4">
                                <div style={{ fontSize: "13px", opacity: "0.9" }}>Status</div>
                                <div style={{ fontSize: "18px", fontWeight: "700" }}>{fetchResult.status}</div>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "15px" }}>
                            <Col md="12">
                                <div style={{ fontSize: "14px", opacity: "0.95" }}>
                                    <strong>Message:</strong> {fetchResult.message}
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}
            </div>
        );
    };

    const getEmailStatusTag = function (status) {
        var upperStatus = (status || "").toUpperCase();
        var label, bg, color;

        if (upperStatus === "") {
            label = "Not Extracted";
            bg = "#e0e0e0";
            color = "#555";
        } else if (upperStatus === "NEW") {
            label = "Extracted";
            bg = "#d0eaff";
            color = "#1565c0";
        } else if (upperStatus === "ERROR") {
            label = "Extraction Issue";
            bg = "#fde0e0";
            color = "#c62828";
        } else if (upperStatus === "PROCESSED") {
            label = "Processed";
            bg = "#d4edda";
            color = "#1a6b3a";
        } else {
            label = status;
            bg = "#f0f0f0";
            color = "#444";
        }

        return (
            <span
                style={{
                    display: "inline-block",
                    padding: "2px 10px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "700",
                    background: bg,
                    color: color,
                    letterSpacing: "0.3px",
                    marginLeft: "8px",
                    verticalAlign: "middle",
                }}
            >
                {label}
            </span>
        );
    };

    const renderEmailCards = function () {
        if (emails.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                    <i className="fa fa-inbox" style={{ fontSize: "48px", marginBottom: "15px", opacity: "0.5" }}></i>
                    <div style={{ fontSize: "16px" }}>No emails found. Click "Search" to fetch emails.</div>
                </div>
            );
        }

        return (
            <div>
                <div style={{ marginBottom: "15px" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#667eea" }}>
                        <i className="fa fa-envelope" style={{ marginRight: "8px" }}></i>
                        {emails.length} Email(s) Found
                    </span>
                </div>

                {emails.map(function (email, index) {
                    return (
                        <Card
                            key={email.id || index}
                            style={{
                                borderRadius: "12px",
                                border: "1px solid #e8e8e8",
                                marginBottom: "15px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                overflow: "hidden",
                            }}
                        >
                            <CardHeader
                                style={{
                                    background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                                    padding: "12px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                }}
                            >
                                <Row style={{ alignItems: "center" }}>
                                    <Col md="7">
                                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                                            {email.subject}
                                            {getEmailStatusTag(email.status)}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                            <i className="fa fa-user" style={{ marginRight: "5px" }}></i>
                                            From: {email.senderEmail}
                                        </div>
                                    </Col>
                                    <Col md="5" style={{ textAlign: "right" }}>
                                        <div style={{ fontSize: "12px", color: "#999" }}>
                                            <i className="fa fa-calendar" style={{ marginRight: "5px" }}></i>
                                            {moment(email.receivedDate).format("DD MMM YYYY HH:mm")}
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>

                            <CardBody style={{ padding: "16px", backgroundColor: "#fafafa" }}>
                                <div
                                    style={{
                                        fontSize: "13px",
                                        lineHeight: "1.6",
                                        color: "#555",
                                        fontFamily: "monospace",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        backgroundColor: "#fff",
                                        padding: "12px",
                                        borderRadius: "6px",
                                        border: "1px solid #f0f0f0",
                                        maxHeight: "250px",
                                        overflowY: "auto",
                                    }}
                                >
                                    {email.body}
                                </div>

                                <Row
                                    style={{
                                        marginTop: "12px",
                                        fontSize: "12px",
                                        color: "#999",
                                        borderTop: "1px solid #f0f0f0",
                                        paddingTop: "12px",
                                    }}
                                >
                                    <Col md="6">
                                        <div>
                                            <strong>Source Email:</strong> {email.sourceEmail}
                                        </div>
                                        <div style={{ marginTop: "4px" }}>
                                            <strong>Message ID:</strong> <span style={{ fontSize: "11px" }}>{email.messageId}</span>
                                        </div>
                                    </Col>
                                    <Col md="6" style={{ textAlign: "right" }}>
                                        <div>
                                            <strong>Received:</strong> {moment(email.receivedDate).format("DD MMM YYYY HH:mm:ss")}
                                        </div>
                                        <div style={{ marginTop: "4px" }}>
                                            <strong>Fetched:</strong> {moment(email.fetchedDate).format("DD MMM YYYY HH:mm:ss")}
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderContentTab = function () {
        return (
            <div>
                {contentLoading && <LoadingOverlay />}

                <Row className="mb-4">
                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                Email Address <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="select"
                            name="emailAddress"
                            value={contentForm.emailAddress}
                            onChange={handleContentFormChange}
                            disabled={contentLocked}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        >
                            <option value="">-- Select Email --</option>
                            {loadOptions.sourceEmails.map(function (email) {
                                return (
                                    <option key={email.id} value={email.key}>
                                        {email.value} ({email.key})
                                    </option>
                                );
                            })}
                        </Input>
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                Start Date <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="date"
                            name="startDate"
                            value={contentForm.startDate}
                            onChange={handleContentFormChange}
                            disabled={contentLocked}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        />
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#333" }}>
                                End Date <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="date"
                            name="endDate"
                            value={contentForm.endDate}
                            onChange={handleContentFormChange}
                            disabled={contentLocked}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        />
                    </Col>

                    <Col md="3">
                        <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontWeight: "600", fontSize: "13px", color: "#666" }}>
                                Sender Email (Mandatory for Extract) <span style={{ color: "#eb3349" }}>*</span>
                            </label>
                        </div>
                        <Input
                            type="select"
                            name="senderEmailaddress"
                            value={contentForm.senderEmailaddress}
                            onChange={handleContentFormChange}
                            disabled={contentLocked}
                            style={{ borderRadius: "8px", border: "1px solid #e0e0e0", padding: "8px 12px" }}
                        >
                            <option value="">-- Select Sender --</option>
                            {loadOptions.fromAddresses.map(function (address) {
                                return (
                                    <option key={address.id} value={address.key}>
                                        {address.value} ({address.key})
                                    </option>
                                );
                            })}
                        </Input>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col md="12">
                        <Button
                            style={ButtonStyles.primarySm}
                            onClick={handleContentSubmit}
                            className="mr-2"
                            disabled={contentLocked || apiBusy}
                        >
                            <i className="fa fa-search" style={{ marginRight: "5px" }}></i>
                            Submit
                        </Button>
                        <Button
                            style={ButtonStyles.successSm}
                            onClick={handleExtract}
                            className="mr-2"
                            disabled={apiBusy || emails.length === 0}
                        >
                            <i className="fa fa-cogs" style={{ marginRight: "5px" }}></i>
                            Extract
                        </Button>
                        <Button style={ButtonStyles.secondarySm} onClick={handleContentClear} disabled={apiBusy}>
                            <i className="fa fa-times" style={{ marginRight: "5px" }}></i>
                            Clear
                        </Button>
                    </Col>
                </Row>

                {renderEmailCards()}
            </div>
        );
    };

    const renderExtractGrid = function () {
        if (extractRows.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                    <i className="fa fa-database" style={{ fontSize: "44px", marginBottom: "12px", opacity: "0.5" }}></i>
                    <div style={{ fontSize: "16px" }}>No extracted rows available.</div>
                </div>
            );
        }

        return (
            <div style={{ overflowX: "auto", width: "100%" }}>
                <Table bordered hover responsive size="sm" style={{ background: "#fff", width: "100%", tableLayout: "fixed" }}>
                    <thead style={{ background: "#f3f5ff" }}>
                        <tr>
                            <th>Message ID</th>
                            <th>Invoice Number</th>
                            <th>Balaence</th>
                            <th>Invoice Value</th>
                            <th>Credited Amount</th>
                            <th>Difference</th>
                            <th>Reference</th>
                            <th>Remarks</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {extractRows.map(function (row) {
                            return (
                                <tr key={row.id !== null && row.id !== undefined ? row.id : row.rowId}>
                                    <td style={{ maxWidth: "180px", wordBreak: "break-word" }}>{row.messageId}</td>
                                    <td>
                                        {row.isEditing && (
                                            <Input
                                                type="text"
                                                value={row.invoiceNumber}
                                                onChange={function (e) {
                                                    handleExtractRowChange(row.rowId, "invoiceNumber", e.target.value);
                                                }}
                                            />
                                        )}
                                        {!row.isEditing && row.invoiceNumber}
                                    </td>
                                    <td>{toTwoDecimals(row.balance).toFixed(2)}</td>
                                    <td>
                                        {row.isEditing && (
                                            <Input
                                                type="number"
                                                value={row.invoiceValue}
                                                onChange={function (e) {
                                                    handleExtractRowChange(row.rowId, "invoiceValue", e.target.value);
                                                }}
                                            />
                                        )}
                                        {!row.isEditing && toTwoDecimals(row.invoiceValue).toFixed(2)}
                                    </td>
                                    <td>{toTwoDecimals(row.creditedAmount).toFixed(2)}</td>
                                    <td>{toTwoDecimals(row.difference).toFixed(2)}</td>
                                    <td>{row.transactionReference}</td>
                                    <td style={{ maxWidth: "220px", wordBreak: "break-word" }}>{row.remarks}</td>
                                    <td>
                                        <span
                                            style={{
                                                fontWeight: "600",
                                                color: row.status === "ERROR" ? "#eb3349" : "#1a8f5f",
                                            }}
                                        >
                                            {row.status}
                                        </span>
                                    </td>
                                    <td>
                                        <Button
                                            style={{
                                                ...(row.isEditing ? ButtonStyles.primarySm : ButtonStyles.warningSm),
                                                marginRight: "8px",
                                                marginBottom: "6px",
                                            }}
                                            onClick={function () {
                                                toggleEditRow(row.rowId);
                                            }}
                                            disabled={apiBusy}
                                        >
                                            {row.isEditing ? "Cancel" : "Edit"}
                                        </Button>
                                        {row.isEditing && (
                                            <Button
                                                style={{ ...ButtonStyles.primarySm, marginRight: "8px", marginBottom: "6px" }}
                                                onClick={function () {
                                                    verifyRow(row.rowId);
                                                }}
                                                disabled={apiBusy}
                                            >
                                                Verify
                                            </Button>
                                        )}
                                        {!row.isEditing && (
                                            <Button
                                                style={{ ...ButtonStyles.successSm, marginBottom: "6px" }}
                                                onClick={function () {
                                                    processRow(row.rowId);
                                                }}
                                                disabled={apiBusy || (row.status || "").toUpperCase() === "ERROR"}
                                            >
                                                Process
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        );
    };

    const renderExtractTab = function () {
        const isAnyRowEditing = extractRows.some(function (row) {
            return row.isEditing;
        });
        const hasErrorRows = extractRows.some(function (row) {
            return (row.status || "").toUpperCase() === "ERROR";
        });

        return (
            <div>
                {extractLoading && <LoadingOverlay />}

                {extractSummary && (
                    <Card
                        style={{
                            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                            borderRadius: "12px",
                            border: "none",
                            padding: "18px",
                            color: "#fff",
                            marginBottom: "16px",
                        }}
                    >
                        <Row>
                            <Col md="3">
                                <div style={{ fontSize: "12px", opacity: "0.9" }}>Total Emails</div>
                                <div style={{ fontSize: "28px", fontWeight: "700" }}>{extractSummary.totalEmails}</div>
                            </Col>
                            <Col md="3">
                                <div style={{ fontSize: "12px", opacity: "0.9" }}>Saved</div>
                                <div style={{ fontSize: "28px", fontWeight: "700" }}>{extractSummary.savedCount}</div>
                            </Col>
                            <Col md="3">
                                <div style={{ fontSize: "12px", opacity: "0.9" }}>Duplicate</div>
                                <div style={{ fontSize: "28px", fontWeight: "700" }}>{extractSummary.duplicateCount}</div>
                            </Col>
                            <Col md="3">
                                <div style={{ fontSize: "12px", opacity: "0.9" }}>Error</div>
                                <div style={{ fontSize: "28px", fontWeight: "700" }}>{extractSummary.errorCount}</div>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "8px", fontSize: "12px" }}>
                            <Col md="12">
                                <strong>Filter:</strong> {extractSummary.emailAddress} | {extractSummary.senderEmailaddress} |
                                {extractSummary.startDate ? " " + moment(extractSummary.startDate).format("YYYY-MM-DD") : ""} to
                                {extractSummary.endDate ? " " + moment(extractSummary.endDate).format("YYYY-MM-DD") : ""}
                            </Col>
                        </Row>
                    </Card>
                )}

                <Row className="mb-3">
                    <Col md="12">
                        <Button
                            style={{ ...ButtonStyles.successSm, marginRight: "8px", marginBottom: "6px" }}
                            onClick={processAllRows}
                            disabled={apiBusy || isAnyRowEditing || hasErrorRows}
                        >
                            <i className="fa fa-check-circle" style={{ marginRight: "5px" }}></i>
                            Process All
                        </Button>
                        <Button
                            style={{ ...ButtonStyles.secondarySm, marginBottom: "6px" }}
                            onClick={loadExtractDetailsByStatus}
                            disabled={apiBusy}
                        >
                            <i className="fa fa-sync" style={{ marginRight: "5px" }}></i>
                            Refresh Status Data
                        </Button>
                    </Col>
                </Row>

                {renderExtractGrid()}
            </div>
        );
    };

    const getTab1Style = function () {
        return {
            cursor: "pointer",
            color: activeTab === 1 ? "#667eea" : "#999",
            borderBottom: activeTab === 1 ? "3px solid #667eea" : "none",
            fontWeight: activeTab === 1 ? "600" : "500",
            fontSize: "15px",
            paddingBottom: "12px",
        };
    };

    const getTab2Style = function () {
        return {
            cursor: "pointer",
            color: activeTab === 2 ? "#667eea" : "#999",
            borderBottom: activeTab === 2 ? "3px solid #667eea" : "none",
            fontWeight: activeTab === 2 ? "600" : "500",
            fontSize: "15px",
            paddingBottom: "12px",
        };
    };

    const getTab3Style = function () {
        return {
            cursor: "pointer",
            color: activeTab === 3 ? "#667eea" : "#999",
            borderBottom: activeTab === 3 ? "3px solid #667eea" : "none",
            fontWeight: activeTab === 3 ? "600" : "500",
            fontSize: "15px",
            paddingBottom: "12px",
        };
    };

    return (
        <div>
            {block && <LoadingOverlay />}

            <Card
                style={{
                    borderRadius: "15px",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                    border: "none",
                    overflow: "hidden",
                }}
            >
                <CardHeader
                    style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        padding: "20px 25px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "none",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <i className="fa fa-envelope" style={{ fontSize: "24px", color: "#fff" }}></i>
                        <strong style={{ fontSize: "1.2rem", color: "#fff" }}>Email Sync Center</strong>
                    </div>
                </CardHeader>

                <Nav tabs style={{ borderBottom: "2px solid #f0f0f0", paddingLeft: "25px" }}>
                    <NavItem>
                        <NavLink active={activeTab === 1} onClick={function () { handleTabChange(1); }} style={getTab1Style()}>
                            <i className="fa fa-sync" style={{ marginRight: "8px" }}></i>
                            Email Fetch
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink active={activeTab === 2} onClick={function () { handleTabChange(2); }} style={getTab2Style()}>
                            <i className="fa fa-envelope-open" style={{ marginRight: "8px" }}></i>
                            Email Content
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink active={activeTab === 3} onClick={function () { handleTabChange(3); }} style={getTab3Style()}>
                            <i className="fa fa-database" style={{ marginRight: "8px" }}></i>
                            Extracted Details
                        </NavLink>
                    </NavItem>
                </Nav>

                <CardBody style={{ padding: "25px" }}>
                    {activeTab === 1 && renderFetchTab()}
                    {activeTab === 2 && renderContentTab()}
                    {activeTab === 3 && renderExtractTab()}
                </CardBody>
            </Card>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/components/CustomComponents/EmailSyncCenter.js